import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Route, Routes, useNavigate, useParams } from "react-router-dom";

import { ApiError } from "../../api/client";
import { useMe } from "../../auth/useMe";
import { HierarchyApi } from "../../admin/hierarchy/api";
import { useScopeNames } from "../../admin/hierarchy/useScopeNames";
import { useMarkSeen } from "../../notifications/useMarkSeen";
import { Wizard, WizardField, WizardSection, ScopePicker, type WizardStep } from "../../wizard/Wizard";
import { ThreatStepBody, type ThreatTo } from "../../wizard/ThreatStep";
import { ResolutionPanel } from "../../shell/ResolutionPanel";
import { AssignAsTaskButton } from "../../shell/AssignAsTaskButton";
import {
  POIApi,
  type Classification,
  type POI,
  type POIScopeLevel,
  type POIStatus,
  type ThreatLevel,
} from "./api";

export function POIRoutes() {
  return (
    <Routes>
      <Route index element={<POIList />} />
      <Route path="new" element={<NewPOI />} />
      <Route path=":id" element={<POIDetail />} />
    </Routes>
  );
}

function POIList() {
  const [status, setStatus] = useState<POIStatus | "">("");
  const [threat, setThreat] = useState<ThreatLevel | "">("");
  const [search, setSearch] = useState("");
  const items = useQuery({
    queryKey: ["poi", status, threat],
    queryFn: () =>
      POIApi.list({
        status: (status || undefined) as POIStatus | undefined,
        threatLevel: (threat || undefined) as ThreatLevel | undefined,
      }),
  });
  useMarkSeen("poi", items.dataUpdatedAt);
  const { branchName, wardName, constituencyName } = useScopeNames();
  function scopeLabel(p: POI): string {
    if (p.scopeLevel === "branch") return branchName(p.scopeId);
    if (p.scopeLevel === "ward") return wardName(p.scopeId);
    return constituencyName(p.scopeId);
  }
  const list = (items.data?.items ?? [])
    .filter((p) => !search || (p.fullName + " " + (p.aliases ?? []).join(" ")).toLowerCase().includes(search.toLowerCase()))
    .slice()
    .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));

  return (
    <div>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 4 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>People of Interest</h1>
          <p className="muted" style={{ margin: 0, fontSize: 14 }}>A list of people being watched. Add a new entry, or look up someone you've seen before.</p>
        </div>
        <Link to="new"><button>Add POI</button></Link>
      </header>

      <div className="row-3" style={{ marginTop: 16 }}>
        <label>
          Status:
          <select value={status} onChange={(e) => setStatus(e.target.value as POIStatus | "")}>
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="dormant">Dormant</option>
            <option value="cleared">Cleared</option>
          </select>
        </label>
        <label>
          Threat:
          <select value={threat} onChange={(e) => setThreat(e.target.value as ThreatLevel | "")}>
            <option value="">All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </label>
        <label>
          Search:
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or alias…" />
        </label>
      </div>

      {items.isLoading && <p>Loading…</p>}
      {items.error && <p className="error">{(items.error as Error).message}</p>}

      <div className="card-grid">
        {list.map((p) => (
          <Link key={p.id} to={p.id} className="list-card">
            <div className="list-card-header">
              <ThreatBadge level={p.threatLevel} />
              <StatusBadge status={p.status} />
              <ClassificationBadge level={p.classification} />
            </div>
            <div className="list-card-title">{p.fullName}</div>
            <div className="list-card-snippet">
              {p.aliases?.length > 0 ? <>AKA: {p.aliases.join(", ")}</> : "No aliases."}
            </div>
            <div className="list-card-foot">
              <span>{p.scopeLevel}: {scopeLabel(p)}</span>
              <span>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : "—"}</span>
            </div>
          </Link>
        ))}
        {list.length === 0 && !items.isLoading && (
          <div className="muted" style={{ padding: 16, gridColumn: "1 / -1" }}>No matching records.</div>
        )}
      </div>
    </div>
  );
}

function POIDetail() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { isSuperadmin } = useMe();
  const { branchName, wardName, constituencyName } = useScopeNames();
  const [error, setError] = useState<string | null>(null);

  const p = useQuery({ queryKey: ["poi", id], queryFn: () => POIApi.get(id), enabled: !!id });

  const setStatus = useMutation({
    mutationFn: (next: POIStatus) => POIApi.update(id, { status: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["poi", id] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });
  const remove = useMutation({
    mutationFn: () => POIApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["poi"] });
      nav("..", { replace: true });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });

  if (p.isLoading) return <p>Loading…</p>;
  if (p.error) return <p className="error">{(p.error as Error).message}</p>;
  if (!p.data) return null;

  return (
    <div className="hierarchy">
      <header>
        <Link to=".." className="muted">← All POI</Link>
        <h1 style={{ marginTop: 4 }}>
          {p.data.fullName}{" "}
          <ThreatBadge level={p.data.threatLevel} />
          <StatusBadge status={p.data.status} />
          <ClassificationBadge level={p.data.classification} />
        </h1>
      </header>
      {error && <div className="error">{error}</div>}

      <div className="card">
        <h3 className="detail-section-title">Identity</h3>
        <dl className="kv">
          <dt>Aliases</dt><dd>{p.data.aliases?.length ? p.data.aliases.join(", ") : "—"}</dd>
          <dt>National ID</dt><dd>{p.data.idNumber || "—"}</dd>
          <dt>Date of birth</dt><dd>{p.data.dateOfBirth || "—"}</dd>
          <dt>Occupation</dt><dd>{p.data.occupation || "—"}</dd>
        </dl>
      </div>

      <div className="card detail-section">
        <h3 className="detail-section-title">Affiliation + Surveillance</h3>
        <dl className="kv">
          <dt>Affiliation</dt><dd style={{ whiteSpace: "pre-wrap" }}>{p.data.affiliation || "—"}</dd>
          <dt>Last known location</dt><dd style={{ whiteSpace: "pre-wrap" }}>{p.data.lastKnownLocation || "—"}</dd>
          <dt>Known associates</dt><dd style={{ whiteSpace: "pre-wrap" }}>{p.data.knownAssociates || "—"}</dd>
        </dl>
      </div>

      <div className="card detail-section">
        <h3 className="detail-section-title">Contact</h3>
        <dl className="kv">
          <dt>Phone</dt><dd>{p.data.phone || "—"}</dd>
          <dt>Email</dt><dd>{p.data.email || "—"}</dd>
          <dt>Address</dt><dd>{p.data.address || "—"}</dd>
        </dl>
      </div>

      <div className="card detail-section">
        <h3 className="detail-section-title">Scope</h3>
        <dl className="kv">
          <dt>Scope level</dt><dd>{p.data.scopeLevel}</dd>
          <dt>Area</dt>
          <dd>
            {p.data.scopeLevel === "branch" && branchName(p.data.scopeId)}
            {p.data.scopeLevel === "ward" && wardName(p.data.scopeId)}
            {p.data.scopeLevel === "constituency" && constituencyName(p.data.scopeId)}
          </dd>
        </dl>
      </div>

      <div className="card detail-section">
        <h3 className="detail-section-title">Notes</h3>
        <p style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.55 }}>
          {p.data.notes || "—"}
        </p>
      </div>

      <ResolutionPanel
        collection="peopleOfInterest"
        recordId={p.data.id}
        current={(p.data as { resolutionStatus?: "pending" | "in_progress" | "resolved" | "false_alarm" }).resolutionStatus}
        onResolved={() => qc.invalidateQueries({ queryKey: ["poi", id] })}
      />

      <div style={{ marginTop: 12 }}>
        <AssignAsTaskButton
          relatedType="poi"
          relatedId={p.data.id}
          suggestedTitle={`Action POI: ${p.data.fullName}`}
        />
      </div>

      <div className="detail-actions">
        {p.data.status !== "cleared" && (
          <button
            className="ghost"
            onClick={() => {
              if (window.confirm(`Mark ${p.data.fullName} as cleared? They'll be hidden from the active watchlist but the record stays.`))
                setStatus.mutate("cleared");
            }}
          >Mark cleared</button>
        )}
        {p.data.status !== "active" && (
          <button
            onClick={() => {
              if (window.confirm(`Reactivate ${p.data.fullName} on the watchlist?`))
                setStatus.mutate("active");
            }}
          >Reactivate</button>
        )}
        {p.data.status !== "dormant" && (
          <button
            className="ghost"
            onClick={() => {
              if (window.confirm(`Mark ${p.data.fullName} dormant? They'll be hidden from active list but the record stays.`))
                setStatus.mutate("dormant");
            }}
          >Mark dormant</button>
        )}
        {isSuperadmin && (
          <button
            className="danger"
            style={{ display: "none" }}
            onClick={() => {/* delete disabled — append-only watchlist */}}
          >Delete</button>
        )}
      </div>
    </div>
  );
}

function NewPOI() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const [fullName, setFullName] = useState("");
  const [aliasesText, setAliasesText] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [occupation, setOccupation] = useState("");
  const [affiliation, setAffiliation] = useState("");
  const [lastKnownLocation, setLastKnownLocation] = useState("");
  const [knownAssociates, setKnownAssociates] = useState("");
  const [notes, setNotes] = useState("");
  const [threatLevel, setThreatLevel] = useState<ThreatLevel>("low");
  const [classification, setClassification] = useState<Classification>("restricted");
  const [scopeLevel, setScopeLevel] = useState<POIScopeLevel>("ward");
  const [threatTo, setThreatTo] = useState<ThreatTo>("none");
  const [threatTarget, setThreatTarget] = useState("");
  const [threatDetails, setThreatDetails] = useState("");

  const scope = useQuery({ queryKey: ["my-scope"], queryFn: HierarchyApi.myScope });
  const [cid, setCid] = useState("");
  const [wid, setWid] = useState("");
  const [bid, setBid] = useState("");
  const constituencies = scope.data?.constituencies ?? [];
  const wardsForCid = (scope.data?.wards ?? []).filter((w) => w.constituencyId === cid);
  const branchesForWid = (scope.data?.branches ?? []).filter((b) => b.wardId === wid);

  const create = useMutation({
    mutationFn: POIApi.create,
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["poi"] });
      nav(`../${p.id}`, { replace: true });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });

  const scopeId =
    scopeLevel === "branch" ? bid : scopeLevel === "ward" ? wid : cid;

  function submit() {
    if (!fullName || !scopeId) return;
    create.mutate({
      fullName,
      aliases: aliasesText.split(",").map((s) => s.trim()).filter(Boolean),
      idNumber: idNumber || undefined,
      phone: phone || undefined,
      email: email || undefined,
      address: address || undefined,
      dateOfBirth: dateOfBirth || undefined,
      occupation: occupation || undefined,
      affiliation: affiliation || undefined,
      lastKnownLocation: lastKnownLocation || undefined,
      knownAssociates: knownAssociates || undefined,
      notes,
      threatLevel,
      classification,
      scopeLevel,
      scopeId,
      threatTo,
      threatTarget: threatTarget || undefined,
      threatDetails: threatDetails || undefined,
    });
  }

  const steps: WizardStep[] = [
    {
      title: "Where",
      canNext: !!scopeId,
      blockerHint: "Pick the scope this POI is watched within",
      body: (
        <WizardSection title="Where is this person watched?" hint="POI records are scoped to a single area; a constituency-scope POI is visible to everyone in any branch under it.">
          <WizardField label="Scope level" hint="Branch is tightest; constituency is broadest. Pick the smallest area this POI is relevant to.">
            <select
              value={scopeLevel}
              onChange={(e) => { setScopeLevel(e.target.value as POIScopeLevel); setWid(""); setBid(""); }}
            >
              <option value="constituency">Constituency-scope</option>
              <option value="ward">Ward-scope</option>
              <option value="branch">Branch-scope</option>
            </select>
          </WizardField>
          <ScopePicker
            constituencies={constituencies}
            wards={wardsForCid}
            branches={branchesForWid}
            cid={cid} wid={wid} bid={bid}
            onCidChange={(v) => { setCid(v); setWid(""); setBid(""); }}
            onWidChange={(v) => { setWid(v); setBid(""); }}
            onBidChange={setBid}
            needBranch={scopeLevel === "branch"}
          />
        </WizardSection>
      ),
    },
    {
      title: "Identity",
      canNext: !!fullName.trim(),
      blockerHint: "Full name is required",
      body: (
        <WizardSection title="Who is this person?" hint="Capture identifiers and what they do for a living. Aliases help match street names against official names later.">
          <WizardField label="Full name">
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="e.g. Felix Kabwe" autoCapitalize="words" />
          </WizardField>
          <WizardField label="Aliases" hint="Comma-separated.">
            <input value={aliasesText} onChange={(e) => setAliasesText(e.target.value)} placeholder="e.g. The Engineer, FK" />
          </WizardField>
          <WizardField label="Date of birth" hint="YYYY-MM-DD; optional.">
            <input value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} placeholder="1985-04-12" spellCheck={false} />
          </WizardField>
          <WizardField label="National ID">
            <input value={idNumber} onChange={(e) => setIdNumber(e.target.value)} placeholder="######/##/#" spellCheck={false} />
          </WizardField>
          <WizardField label="Occupation" hint="What they do for a living. Pastor, journalist, business owner, etc.">
            <input value={occupation} onChange={(e) => setOccupation(e.target.value)} placeholder="e.g. Civil engineer (consultant)" />
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Affiliation",
      canNext: true,
      body: (
        <WizardSection title="Affiliation + surveillance" hint="What's their political position? Where were they last seen? Who do they work with?">
          <WizardField label="Affiliation" hint="Party, faction, organisation, role.">
            <input value={affiliation} onChange={(e) => setAffiliation(e.target.value)} placeholder="e.g. Suspected primary fundraiser for the opposition campaign" />
          </WizardField>
          <WizardField label="Last known location" hint="Where and when you last placed them.">
            <textarea value={lastKnownLocation} onChange={(e) => setLastKnownLocation(e.target.value)} rows={2} placeholder="e.g. Mansa Lodge, evening of 2026-04-30. Black Toyota Fortuner, plate BAA-12." />
          </WizardField>
          <WizardField label="Known associates" hint="People they're often seen with. Name them, link to other POI if relevant.">
            <textarea value={knownAssociates} onChange={(e) => setKnownAssociates(e.target.value)} rows={3} placeholder="e.g. Often seen with Ruth Mwale (POI-2). Three armed bodyguards travel with him." />
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Contact",
      canNext: true,
      body: (
        <WizardSection title="Contact details (optional)" hint="Capture what you know — leave the rest blank.">
          <WizardField label="Phone"><input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+260…" /></WizardField>
          <WizardField label="Email"><input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" /></WizardField>
          <WizardField label="Address"><input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Plot 24, Independence Avenue" /></WizardField>
          <WizardField label="Notes" hint="What you know about their pattern of activity. No speculation.">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} />
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Threat",
      canNext: true,
      body: (
        <WizardSection title="Threat level + classification" hint="High-threat additions push notifications to the chain of command.">
          <WizardField label="Threat level">
            <select value={threatLevel} onChange={(e) => setThreatLevel(e.target.value as ThreatLevel)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </WizardField>
          <WizardField label="Classification" hint="Restricted/Secret POI records are hidden from anyone without case.read_classified.">
            <select value={classification} onChange={(e) => setClassification(e.target.value as Classification)}>
              <option value="unclassified">Unclassified</option>
              <option value="restricted">Restricted</option>
              <option value="secret">Secret</option>
            </select>
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Threat",
      canNext: threatTo === "none" || !!threatTarget.trim(),
      blockerHint: "If they're a threat, capture who's being threatened.",
      body: (
        <ThreatStepBody
          threatTo={threatTo}
          threatTarget={threatTarget}
          threatDetails={threatDetails}
          onThreatTo={setThreatTo}
          onThreatTarget={setThreatTarget}
          onThreatDetails={setThreatDetails}
        />
      ),
    },
    {
      title: "Review",
      canNext: !!fullName.trim() && !!scopeId,
      body: (
        <WizardSection title="Review" hint="Once added, the entry appears in the POI watchlist for everyone in scope.">
          <dl className="kv">
            <dt>Full name</dt><dd>{fullName}</dd>
            <dt>Threat</dt><dd>{threatLevel}</dd>
            <dt>Classification</dt><dd>{classification}</dd>
            <dt>Scope</dt><dd>{scopeLevel} / {scopeId}</dd>
            <dt>Aliases</dt><dd>{aliasesText || "—"}</dd>
            <dt>Notes</dt><dd style={{ whiteSpace: "pre-wrap" }}>{notes || "—"}</dd>
          </dl>
        </WizardSection>
      ),
    },
  ];

  return (
    <div>
      {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}
      <Wizard
        title="Add POI"
        steps={steps}
        busy={create.isPending}
        submitLabel="Add to watchlist"
        onSubmit={submit}
        onCancel={() => nav("..")}
      />
    </div>
  );
}

function ThreatBadge({ level }: { level: ThreatLevel }) {
  const c = level === "high" ? "#b14444" : level === "medium" ? "#a87b1c" : "#3a8053";
  return <span style={{ marginLeft: 8, fontSize: 11, padding: "2px 6px", background: c, color: "white", borderRadius: 4, textTransform: "uppercase" }}>{level} threat</span>;
}

function StatusBadge({ status }: { status: POIStatus }) {
  return <span style={{ marginLeft: 6, fontSize: 11, padding: "2px 6px", background: "var(--border)", color: "var(--text)", borderRadius: 4 }}>{status}</span>;
}

function ClassificationBadge({ level }: { level: Classification }) {
  if (level === "unclassified") return null;
  const c = level === "secret" ? "#b14444" : "#a87b1c";
  return <span style={{ marginLeft: 6, fontSize: 11, padding: "2px 6px", background: c, color: "white", borderRadius: 4, textTransform: "uppercase" }}>{level}</span>;
}
